import { useState } from "react";
import { Youtube, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import { asset } from "@/lib/api";
import { youtubeId } from "@/hooks/useList";
import { AdminCollection } from "./AdminCollection";
import { FileField } from "./FileField";
import { field, Label, FormActions } from "./formBits";

const VideoForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState({ title: "", description: "", source: "upload", url: "", thumbnail_url: "", duration: "", order: 0, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.url) return toast.error(f.source === "youtube" ? "Paste a YouTube link" : "Upload a video file");
    if (f.source === "youtube" && !youtubeId(f.url)) return toast.error("That doesn't look like a YouTube link");
    setBusy(true);
    const { id, created_at, ...body } = f;
    await onSave({ ...body, order: Number(f.order) || 0 });
    setBusy(false);
  };

  return (
    <form onSubmit={submit} data-testid="video-form" className="space-y-5">
      <label className="block"><Label>TITLE</Label><input data-testid="video-title-input" required value={f.title} onChange={(e) => set("title")(e.target.value)} className={field} /></label>
      <label className="block"><Label>DESCRIPTION</Label><textarea data-testid="video-description-input" rows={3} value={f.description} onChange={(e) => set("description")(e.target.value)} className={field} /></label>
      <div>
        <Label>SOURCE</Label>
        <div className="flex gap-2">
          {[["upload", "Upload file", UploadIcon], ["youtube", "YouTube link", Youtube]].map(([k, l, Icon]) => (
            <button type="button" key={k} data-testid={`video-source-${k}`} onClick={() => setF((s) => ({ ...s, source: k, url: s.source === k ? s.url : "" }))}
              className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] tracking-[0.2em] border transition-colors ${f.source === k ? "bg-[#F5A524] text-black border-[#F5A524]" : "border-[#F5A524]/30 text-zinc-300"}`}>
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>
      </div>
      {f.source === "youtube" ? (
        <label className="block"><Label>YOUTUBE LINK</Label><input data-testid="video-youtube-input" value={f.url} onChange={(e) => set("url")(e.target.value)} placeholder="https://youtube.com/watch?v=..." className={field} /></label>
      ) : (
        <FileField label="VIDEO FILE (MP4 / WEBM / MOV, MAX 200MB)" kind="video" accept="video/mp4,video/webm,video/quicktime,.mov,.m4v" value={f.url} onChange={set("url")} testid="video-file" />
      )}
      <FileField label="THUMBNAIL (OPTIONAL)" kind="image" accept="image/*" value={f.thumbnail_url} onChange={set("thumbnail_url")} testid="video-thumb" />
      <div className="grid grid-cols-2 gap-4">
        <label className="block"><Label>LENGTH (E.G. 2:45)</Label><input data-testid="video-duration-input" value={f.duration} onChange={(e) => set("duration")(e.target.value)} className={field} /></label>
        <label className="block"><Label>ORDER</Label><input data-testid="video-order-input" type="number" value={f.order} onChange={(e) => set("order")(e.target.value)} className={field} /></label>
      </div>
      <FormActions busy={busy} onCancel={onCancel} label="SAVE VIDEO" />
    </form>
  );
};

const Preview = ({ item }) => {
  const yt = item.source === "youtube" ? youtubeId(item.url) : null;
  const thumb = item.thumbnail_url ? asset(item.thumbnail_url) : yt ? `https://img.youtube.com/vi/${yt}/default.jpg` : "";
  return (
    <>
      <div className="w-24 aspect-video bg-black shrink-0 overflow-hidden">
        {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : <video src={`${asset(item.url)}#t=0.5`} muted preload="metadata" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold uppercase text-xl truncate">{item.title}</p>
        <p className="font-mono text-[11px] tracking-[0.2em] text-[#F5A524]">{item.source === "youtube" ? "YOUTUBE" : "UPLOAD"}{item.duration ? ` · ${item.duration}` : ""}</p>
      </div>
    </>
  );
};

export const VideosAdmin = () => (
  <AdminCollection name="videos" path="/videos" kicker="TIMELAPSE VIDEOS" title="Timelapses" label="Timelapse" Form={VideoForm} Preview={Preview} />
);
